// src/utils/ga4/analyticsApi.js

import axios from 'axios';
import { auth } from '../../firebase/init';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safemcc-portal-gateway-v2-bvn24355.ew.gateway.dev'

// Helper for getting Firebase auth token
const getAuthTokenForAnalytics = async () => {
    try {
        if (!auth.currentUser) {
            await new Promise(resolve => setTimeout(resolve, 1500)); 
            if (!auth.currentUser) {
                console.error('[analyticsApi.js] getAuthTokenForAnalytics: No authenticated user after delay.');
                throw new Error('No authenticated user for Analytics API');
            }
        }
        const token = await auth.currentUser.getIdToken(true);
        return token;
    } catch (error) {
        console.error('[analyticsApi.js] Failed to get auth token for Analytics');
        throw error;
    }
};

const createAxiosConfig = async (options = {}) => {
    let token;
    try {
        token = await getAuthTokenForAnalytics();
        return {
            ...options,
            headers: {
                ...options.headers,
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        };
    } catch (error) {
        console.error('[analyticsApi.js] Failed to create Axios config for Analytics');
        throw error;
    }
};


/**
 * Fetches the list of GA4 accounts from the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the GA4 accounts.
 * @returns {Promise<Array>} Promise resolving to an array of GA4 account objects.
 */
export const fetchAccountsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/ga4/ga4-accounts';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[analyticsApi.js] Error fetching GA4 accounts`);
        console.error(`[analyticsApi.js] Status: ${error.response?.status}`);
        console.error(`[analyticsApi.js] Code: ${error.code}`);
        console.error(`[analyticsApi.js] Message: ${error.message}`);
        console.error(`[analyticsApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Fetches the list of users with access from the BigQuery table via the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the users with access.
 * @returns {Promise<Array>} Promise resolving to an array of user access objects.
 */
export const fetchUsersFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/ga4/ga4-users';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[analyticsApi.js] Error fetching GA4 users`);
        console.error(`[analyticsApi.js] Status: ${error.response?.status}`);
        console.error(`[analyticsApi.js] Code: ${error.code}`);
        console.error(`[analyticsApi.js] Message: ${error.message}`);
        console.error(`[analyticsApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};



/**
 * Fetches the list of GA4 pending requests from the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the GA4 pending requests.
 * @returns {Promise<Array>} Promise resolving to an array of GA4 pending request objects.
 */
export const fetchPendingRequestsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/ga4/ga4-pending-requests';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[analyticsApi.js] Error fetching GA4 pending requests`);
        console.error(`[analyticsApi.js] Status: ${error.response?.status}`);
        console.error(`[analyticsApi.js] Code: ${error.code}`);
        console.error(`[analyticsApi.js] Message: ${error.message}`);
        console.error(`[analyticsApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Fetches the list of GA4 approved requests from the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the GA4 approved requests.
 * @returns {Promise<Array>} Promise resolving to an array of GA4 approved request objects.
 */
export const fetchApprovedRequestsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/ga4/ga4-approved-requests';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[analyticsApi.js] Error fetching GA4 approved requests`);
        console.error(`[analyticsApi.js] Status: ${error.response?.status}`);
        console.error(`[analyticsApi.js] Code: ${error.code}`);
        console.error(`[analyticsApi.js] Message: ${error.message}`);
        console.error(`[analyticsApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Fetches the list of GA4 rejected requests from the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the GA4 rejected requests.
 * @returns {Promise<Array>} Promise resolving to an array of GA4 rejected request objects.
 */
export const fetchRejectedRequestsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/ga4/ga4-rejected-requests';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[analyticsApi.js] Error fetching GA4 rejected requests`);
        console.error(`[analyticsApi.js] Status: ${error.response?.status}`);
        console.error(`[analyticsApi.js] Code: ${error.code}`);
        console.error(`[analyticsApi.js] Message: ${error.message}`);
        console.error(`[analyticsApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};



/**
 * Fetches the list of GA4 audit logs from the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the audit logs.
 * @returns {Promise<Array>} Promise resolving to an array of GA4 audit log objects.
 */
export const fetchAuditLogsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/ga4/ga4-audit-logs';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[analyticsApi.js] Error fetching GA4 audit logs`);
        console.error(`[analyticsApi.js] Status: ${error.response?.status}`);
        console.error(`[analyticsApi.js] Code: ${error.code}`);
        console.error(`[analyticsApi.js] Message: ${error.message}`);
        console.error(`[analyticsApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Creates an access binding for a user in a GA4 account.
 * @param {Object} requestData - Request data containing: 
 *      operationType, 
 *      accountId, 
 *      accountName, 
 *      requestedBy, 
 *      requestedFor, 
 *      requestedRole,
 *      and requestDetails
 * @returns {Promise<Array>} Promise resolving to an array of GA4 access binding objects.
 */
export const createAccessBindingFromApi = async (requestData) => {
    const endpointPath = '/api/ga4/create-access-binding';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.post(fullUrl, requestData, config);
        return response.data;
    } catch (error) {
        console.error(`[analyticsApi.js] Error creating access binding`);
        console.error(`[analyticsApi.js] Status: ${error.response?.status}`);
        console.error(`[analyticsApi.js] Code: ${error.code}`);
        console.error(`[analyticsApi.js] Message: ${error.message}`);
        console.error(`[analyticsApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Removes an access binding for a user in a GA4 account.
 * @param {Object} requestData - Request data containing: 
 *      operationType, 
 *      accountId, 
 *      accountName, 
 *      requestedBy, 
 *      requestedFor, 
 *      previousRole,
 *      accessBinding
 * @returns {Promise<Array>} Promise resolving to an array of GA4 access binding objects.
 */
export const removeAccessBindingFromApi = async (requestData) => {
    const endpointPath = '/api/ga4/remove-access-binding';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.post(fullUrl, requestData, config);
        return response.data;
    } catch (error) {
        console.error(`[analyticsApi.js] Error removing access binding`);
        console.error(`[analyticsApi.js] Status: ${error.response?.status}`);
        console.error(`[analyticsApi.js] Code: ${error.code}`);
        console.error(`[analyticsApi.js] Message: ${error.message}`);
        console.error(`[analyticsApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};



/**
 * Modifies an access binding for a user in a GA4 account.
 * @param {Object} requestData - Request data containing: 
 *      operationType, 
 *      accountId, 
 *      accountName, 
 *      accessBinding, 
 *      previousRole, 
 *      requestedBy, 
 *      requestedFor, 
 *      requestedRole
 * @returns {Promise<Array>} Promise resolving to an array of GA4 access binding objects.
 */
export const modifyAccessBindingFromApi = async (requestData) => {
    const endpointPath = '/api/ga4/modify-access-binding';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.post(fullUrl, requestData, config);
        return response.data;
    } catch (error) {
        console.error(`[analyticsApi.js] Error modifying access binding`);
        console.error(`[analyticsApi.js] Status: ${error.response?.status}`);
        console.error(`[analyticsApi.js] Code: ${error.code}`);
        console.error(`[analyticsApi.js] Message: ${error.message}`);
        console.error(`[analyticsApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Approves a user's pending access request to a GA4 account.
 * @param {Object} requestData - Request data containing: 
 *      operationType, 
 *      accountId, 
 *      accountName, 
 *      requestId, 
 *      requestedBy, 
 *      requestedFor, 
 *      requestedRole, 
 *      requestedOn, 
 *      approvedBy
 * @returns {Promise<Array>} Promise resolving to an array of GA4 approved request objects.
 */
export const approvePendingRequestFromApi = async (requestData) => {
    const endpointPath = '/api/ga4/approve-access-binding';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.post(fullUrl, requestData, config);
        return response.data;
    } catch (error) {
        console.error(`[analyticsApi.js] Error approving pending request`);
        console.error(`[analyticsApi.js] Status: ${error.response?.status}`);
        console.error(`[analyticsApi.js] Code: ${error.code}`);
        console.error(`[analyticsApi.js] Message: ${error.message}`);
        console.error(`[analyticsApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Rejects a user's pending access request to a GA4 account.
 * @param {Object} requestData - Request data containing: 
 *      operationType, 
 *      accountId, 
 *      accountName, 
 *      requestId, 
 *      requestedBy, 
 *      requestedFor, 
 *      requestedRole, 
 *      requestedOn, 
 *      rejectedBy
 * @returns {Promise<Array>} Promise resolving to an array of GA4 rejected request objects.
 */
export const rejectPendingRequestFromApi = async (requestData) => {
    const endpointPath = '/api/ga4/reject-access-binding';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.post(fullUrl, requestData, config);
        return response.data;
    } catch (error) {
        console.error(`[analyticsApi.js] Error rejecting pending request`);
        console.error(`[analyticsApi.js] Status: ${error.response?.status}`);
        console.error(`[analyticsApi.js] Code: ${error.code}`);
        console.error(`[analyticsApi.js] Message: ${error.message}`);
        console.error(`[analyticsApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Creates a batch of access bindings for user(s) to GA4 account(s). Uses same endpoint as createAccessBindingFromApi.
 * @param {Object} requestData - Request data containing: 
 *      operationType, 
 *      requestedBy,
 *      requestDetails,
 *      batchRequests
 * @returns {Promise<Array>} Promise resolving to an array of GA4 access binding objects.
 */
export const createBatchAccessBindingsFromApi = async (requestData) => {
    const endpointPath = '/api/ga4/create-access-binding';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.post(fullUrl, requestData, config);
        return response.data;
    } catch (error) {
        console.error(`[analyticsApi.js] Error creating batch access bindings`);
        console.error(`[analyticsApi.js] Status: ${error.response?.status}`);
        console.error(`[analyticsApi.js] Code: ${error.code}`);
        console.error(`[analyticsApi.js] Message: ${error.message}`);
        console.error(`[analyticsApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};
